// Slimme laag: regels die uit de bestaande data afleiden wat je nu het beste kunt doen.
// Bewust zonder AI-aanroep: gratis, direct, voorspelbaar en testbaar. Elk advies zegt waarom.
// Geeft lijsten terug; het scherm bepaalt hoe ze getoond worden. Er wordt niets automatisch uitgevoerd.
import { afr, plusDagen, vandaagIso } from "./gedeeld";

const DAG = 86400000;
const dagenGeleden = (iso, nu) => (iso ? Math.floor((nu - new Date(iso).getTime()) / DAG) : null);
const naamVan = (l) => (l && l.company && l.company !== String.fromCharCode(8212) ? l.company : (l && l.name) || "lead");
const TOT_VOORSTEL = ["qualified", "proposal", "demo", "offerte", "prospect", "interesse"];
const GEWONNEN = ["won", "gewonnen"];
const VERLOREN = ["lost", "verloren"];

export const ERNST = { hoog: 0, middel: 1, laag: 2 };

export function maakAdvies({ leads = [], offertes = [], facturen = [], diensten = [], tickets = [], taken = [], laatsteActiviteit = {}, nu = Date.now() }) {
  const advies = [];
  // Laatste teken van leven: de nieuwste van de laatste activiteit, een eventuele wijzigingsdatum en het aanmaken
  const beweging = (l) => [laatsteActiviteit[String(l.id)], l.updated_at, l.created_at].filter(Boolean).sort().pop();
  const leadMap = new Map(leads.map((l) => [String(l.id), l]));
  const metOfferte = new Set(offertes.map((o) => String(o.lead_id)));
  const metFactuur = new Set(facturen.map((f) => String(f.lead_id)));
  const openTaakVoor = new Set(taken.filter((t) => t.status !== "done" && t.lead_id != null).map((t) => String(t.lead_id)));

  // 1. Nieuwe aanvragen: snel reageren is de grootste factor in conversie
  leads.filter((l) => ["cold", "lead"].includes(l.status) && l.created_at && nu - new Date(l.created_at).getTime() < DAG && String(l.source || "").toLowerCase().includes("website"))
    .forEach((l) => advies.push({
      id: "snel-" + l.id, ernst: "hoog", titel: "Reageer vandaag op " + naamVan(l),
      uitleg: "Nieuwe website-aanvraag van minder dan 24 uur oud. Wie binnen een dag reageert, wint vaker.",
      actie: { view: "klanten", klantId: l.id, tekst: "Open dossier" },
    }));

  // 2. Leads die stilvallen in de voorstelfase
  leads.filter((l) => TOT_VOORSTEL.includes(l.status)).forEach((l) => {
    const stil = dagenGeleden(beweging(l), nu);
    if (stil != null && stil >= 7 && !openTaakVoor.has(String(l.id))) {
      advies.push({
        id: "stil-" + l.id, ernst: stil >= 14 ? "hoog" : "middel", titel: naamVan(l) + " staat " + stil + " dagen stil",
        uitleg: "Fase " + l.status + ", geen beweging en geen open taak. Plan een opvolging of sluit hem af.",
        actie: { view: "klanten", klantId: l.id, tekst: "Open dossier" },
      });
    }
  });

  // 3. Gewonnen maar nog geen offerte of factuur: omzet blijft liggen
  leads.filter((l) => GEWONNEN.includes(l.status) && !metFactuur.has(String(l.id)) && dagenGeleden(beweging(l), nu) >= 3)
    .forEach((l) => advies.push({
      id: "factuur-" + l.id, ernst: "hoog", titel: "Nog niets gefactureerd aan " + naamVan(l),
      uitleg: metOfferte.has(String(l.id)) ? "De offerte is geaccepteerd maar er is geen factuur. Maak hem aan." : "Gewonnen zonder offerte of factuur. Leg het vast.",
      actie: { view: "facturatie", klantId: l.id, tekst: metOfferte.has(String(l.id)) ? "Naar facturen" : "Offerte maken" },
    }));

  // 4. Offertes die bijna verlopen
  offertes.filter((o) => o.status === "verstuurd" && o.geldig_tot).forEach((o) => {
    const rest = Math.round((new Date(o.geldig_tot + "T12:00:00").getTime() - nu) / DAG);
    if (rest >= 0 && rest <= 5) advies.push({
      id: "verloopt-" + o.id, ernst: "middel", titel: "Offerte " + (o.nummer || "") + " verloopt over " + rest + " dagen",
      uitleg: "Nog geen antwoord van " + naamVan(leadMap.get(String(o.lead_id))) + ". Bel of mail voordat hij vervalt.",
      actie: { view: "facturatie", tekst: "Naar offertes" },
    });
  });

  // 5. Churn-risico: betalende klant met een ticket dat blijft liggen
  const mrrPerKlant = new Map();
  diensten.filter((x) => x.status === "actief").forEach((x) => mrrPerKlant.set(String(x.lead_id), (mrrPerKlant.get(String(x.lead_id)) || 0) + (Number(x.bedrag_maand) || 0)));
  tickets.filter((t) => t.status === "open" && t.lead_id != null && mrrPerKlant.has(String(t.lead_id)) && dagenGeleden(t.created_at, nu) >= 3)
    .forEach((t) => advies.push({
      id: "churn-" + t.id, ernst: "hoog", titel: "Betalende klant wacht op antwoord: " + naamVan(leadMap.get(String(t.lead_id))),
      uitleg: "Ticket staat " + dagenGeleden(t.created_at, nu) + " dagen open bij een klant die " + Math.round(mrrPerKlant.get(String(t.lead_id))) + " euro per maand betaalt.",
      actie: { view: "klantenservice", tekst: "Naar tickets" },
    }));

  // 6. Afhankelijkheid: een klant is meer dan de helft van de MRR
  const totaalMrr = [...mrrPerKlant.values()].reduce((s, v) => s + v, 0);
  if (mrrPerKlant.size >= 2 && totaalMrr > 0) {
    const [grootsteId, grootste] = [...mrrPerKlant.entries()].sort((a, b) => b[1] - a[1])[0];
    if (grootste / totaalMrr > 0.5) advies.push({
      id: "afhankelijk", ernst: "laag", titel: Math.round((grootste / totaalMrr) * 100) + "% van je MRR komt van " + naamVan(leadMap.get(grootsteId)),
      uitleg: "Zegt die klant op, dan valt de helft van je terugkerende omzet weg. Zoek meer abonnementen.",
      actie: { view: "klanten", klantId: grootsteId, tekst: "Open dossier" },
    });
  }

  // 7. Bron die leads kost maar niets oplevert
  const bronnen = new Map();
  leads.forEach((l) => {
    const b = String(l.source || "onbekend").toLowerCase().split(/[\s[]/)[0] || "onbekend";
    const r = bronnen.get(b) || { n: 0, w: 0 }; r.n += 1; if (GEWONNEN.includes(l.status)) r.w += 1; bronnen.set(b, r);
  });
  bronnen.forEach((r, b) => {
    if (b !== "onbekend" && r.n >= 8 && r.w === 0) advies.push({
      id: "bron-" + b, ernst: "laag", titel: "Kanaal '" + b + "' levert veel leads maar nog geen klant",
      uitleg: r.n + " leads, 0 gewonnen. Verbeter de opvolging van dit kanaal of stop met investeren.", actie: { view: "vandaag", tekst: "" },
    });
  });

  return advies.sort((a, b) => ERNST[a.ernst] - ERNST[b.ernst]);
}

// Verwachte inkomsten in de komende 30 dagen: openstaande facturen (naar vervaldatum) plus een maand MRR.
// Achterstallige facturen tellen mee met een kans van 70%, omdat ze vaak toch binnenkomen maar niet zeker zijn.
export function kasvoorspelling({ facturen = [], diensten = [], vandaag = vandaagIso() }) {
  const grens = plusDagen(vandaag, 30);
  let verwacht = 0; let achterstallig = 0; let zeker = 0;
  facturen.filter((f) => f.payment_status !== "betaald").forEach((f) => {
    const bedrag = Number(f.amount_incl) || 0;
    if (f.vervaldatum && f.vervaldatum < vandaag) { achterstallig += bedrag; verwacht += bedrag * 0.7; }
    else if (!f.vervaldatum || f.vervaldatum <= grens) { zeker += bedrag; verwacht += bedrag; }
  });
  const mrr = diensten.filter((x) => x.status === "actief").reduce((s, x) => s + (Number(x.bedrag_maand) || 0), 0);
  return { verwacht: afr(verwacht + mrr * 1.21), zeker: afr(zeker), achterstallig: afr(achterstallig), mrrIncl: afr(mrr * 1.21) };
}

// Af te dragen btw in het lopende kwartaal: ontvangen btw over omzet min btw op kosten. Reserveer dit bedrag.
export function btwReserve(trans = [], vandaag = vandaagIso()) {
  const d = new Date(vandaag + "T12:00:00");
  const q = Math.ceil((d.getMonth() + 1) / 3);
  const sleutel = "Q" + q + "-" + d.getFullYear();
  const rijen = trans.filter((t) => t.quarter === sleutel);
  const ontvangen = rijen.filter((t) => t.type === "income").reduce((s, t) => s + (Number(t.vat_amount) || 0), 0);
  const voorbelasting = rijen.filter((t) => t.type !== "income").reduce((s, t) => s + (Number(t.vat_amount) || 0), 0);
  return { kwartaal: sleutel, afdragen: afr(ontvangen - voorbelasting), ontvangen: afr(ontvangen), voorbelasting: afr(voorbelasting) };
}
